import React, { useCallback, useEffect, useState } from 'react'
import { PageHeader } from '@/components/layout'
import { adminService, type Instructor } from '@/services/adminService'
import { useDeleteUser, useUpdateUser, useResetUserTOTP } from '@/hooks/useUsers'
import { CreateInstructorModal } from '@/components/admin/CreateInstructorModal'

export interface AdminInstructorsPageProps {
  service?: typeof adminService
}

/**
 * AdminInstructorsPage lists all instructors (role=instructor) and lets
 * admins with the `user.create` permission mint new ones. Per the business
 * model, an instructor is a single-tier account created by admins — never
 * "promoted" from a student.
 */
export const AdminInstructorsPage: React.FC<AdminInstructorsPageProps> = ({
  service = adminService,
}) => {
  const [list, setList] = useState<Instructor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [togglingUser, setTogglingUser] = useState<Instructor | null>(null)
  const [deletingUser, setDeletingUser] = useState<Instructor | null>(null)
  const [resettingTOTPUser, setResettingTOTPUser] = useState<Instructor | null>(null)

  const deleteMutation = useDeleteUser()
  const updateMutation = useUpdateUser()
  const resetTOTPMutation = useResetUserTOTP()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await service.listInstructors()
      if (res.success && res.data) setList(res.data)
    } catch (err) {
      const e = err as Error
      setToast({ type: 'error', text: e.message || 'Gagal memuat guru' })
    } finally {
      setLoading(false)
    }
  }, [service])

  useEffect(() => {
    load()
  }, [load])

  const filteredInstructors = list.filter((u) => {
    const q = searchQuery.toLowerCase()
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
  })

  return (
    <div data-testid="admin-instructors-page">
      <PageHeader
        title="Kelola Guru"
        subtitle="Tambah dan kelola guru/instruktur platform"
        actions={
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:scale-105 transition-transform"
            data-testid="add-instructor-button"
          >
            Tambah Guru
          </button>
        }
      />

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-0 md:max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-500 text-[20px]">
            search
          </span>
          <input
            type="text"
            placeholder="Cari guru..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50"
          />
        </div>
      </div>

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          data-testid="instructor-toast"
          className={`mb-4 p-3 rounded ${
            toast.type === 'success'
              ? 'bg-green-500/10 text-green-300'
              : 'bg-red-500/10 text-red-300'
          }`}
        >
          {toast.text}
        </div>
      )}

      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[750px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-white/5">
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Nama</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Email</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Telepon</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Status</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden lg:table-cell">Dibuat</th>
                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/5">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                    Memuat...
                  </td>
                </tr>
              )}
              {!loading && filteredInstructors.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                    {searchQuery ? 'Tidak ada guru yang cocok.' : 'Belum ada guru.'}
                  </td>
                </tr>
              )}
              {filteredInstructors.map((u) => (
                <tr
                  key={u.id}
                  className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                  data-testid={`instructor-row-${u.id}`}
                >
                  <td className="px-5 py-4 text-sm text-gray-900 dark:text-white">{u.name}</td>
                  <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">{u.email}</td>
                  <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">{u.phone || '-'}</td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                      u.isActive
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-400 text-white'
                    }`}>
                      {u.isActive ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-500 dark:text-text-muted hidden lg:table-cell">
                    {new Date(u.createdAt).toLocaleDateString('id-ID')}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setTogglingUser(u)}
                        className={`size-10 md:size-8 rounded-lg flex items-center justify-center text-white transition-opacity ${
                          u.isActive
                            ? 'bg-amber-500 hover:opacity-90'
                            : 'bg-emerald-500 hover:opacity-90'
                        }`}
                        title={u.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                      >
                        <span className="material-symbols-outlined text-[16px]">
                          {u.isActive ? 'block' : 'check_circle'}
                        </span>
                      </button>
                      <button
                        onClick={() => setDeletingUser(u)}
                        className="size-10 md:size-8 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center hover:opacity-90 transition-opacity"
                        title="Hapus"
                      >
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                      <button
                        onClick={() => setResettingTOTPUser(u)}
                        className="size-10 md:size-8 rounded-lg bg-purple-500 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                        title="Reset TOTP"
                      >
                        <span className="material-symbols-outlined text-[16px]">phonelink_lock</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {togglingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setTogglingUser(null)} />
          <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {togglingUser.isActive ? 'Nonaktifkan Guru' : 'Aktifkan Guru'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {togglingUser.isActive ? (
                <>Nonaktifkan <span className="font-medium text-gray-900 dark:text-white">{togglingUser.name}</span>? Guru tidak akan bisa login sampai diaktifkan kembali.</>
              ) : (
                <>Aktifkan <span className="font-medium text-gray-900 dark:text-white">{togglingUser.name}</span>? Guru akan bisa login kembali.</>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setTogglingUser(null)}
                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  updateMutation.mutate({
                    id: togglingUser.id,
                    data: { isActive: !togglingUser.isActive },
                  })
                  setTogglingUser(null)
                }}
                disabled={updateMutation.isPending}
                className={`px-4 py-2 text-white font-bold rounded-lg transition-opacity disabled:opacity-50 ${
                  togglingUser.isActive
                    ? 'bg-amber-500 hover:opacity-90'
                    : 'bg-emerald-500 hover:opacity-90'
                }`}
              >
                {updateMutation.isPending ? 'Memproses...' : (togglingUser.isActive ? 'Nonaktifkan' : 'Aktifkan')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeletingUser(null)} />
          <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hapus Guru</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Hapus <span className="font-medium text-gray-900 dark:text-white">{deletingUser.name}</span> secara permanent? Aksi ini tidak dapat dibatalkan.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingUser(null)}
                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  deleteMutation.mutate(deletingUser.id)
                  setDeletingUser(null)
                }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-rose-500 hover:opacity-90 text-white font-bold rounded-lg transition-opacity disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Memproses...' : 'Hapus Permanent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resettingTOTPUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setResettingTOTPUser(null)} />
          <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Reset TOTP</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Reset TOTP untuk <span className="font-medium text-gray-900 dark:text-white">{resettingTOTPUser.name}</span>? Guru harus scan ulang QR code Google Authenticator saat login berikutnya.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setResettingTOTPUser(null)}
                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  resetTOTPMutation.mutate(resettingTOTPUser.id)
                  setResettingTOTPUser(null)
                }}
                disabled={resetTOTPMutation.isPending}
                className="px-4 py-2 bg-purple-500 hover:opacity-90 text-white font-bold rounded-lg transition-opacity disabled:opacity-50"
              >
                {resetTOTPMutation.isPending ? 'Memproses...' : 'Reset TOTP'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateInstructorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setToast({ type: 'success', text: 'Guru berhasil dibuat' })
          load()
        }}
        service={service}
      />
    </div>
  )
}

export default AdminInstructorsPage
